export type ID = string

/** `YYYY-MM-DD` in the viewer's local time. The calendar-day key used everywhere. */
export type DayKey = string

export type Role = 'owner' | 'admin' | 'manager' | 'member'
export type Screen =
  | 'timer'
  | 'prospects'
  | 'timesheet'
  | 'reports'
  | 'projects'
  | 'clients'
  | 'team'
  | 'settings'

export const ROLE_LABEL: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Member',
}

export const ROLE_BLURB: Record<Role, string> = {
  owner: 'Owner — full control, can’t be removed',
  admin: 'Admin — manages projects, people and rules',
  manager: 'Manager — sees and exports everyone’s time',
  member: 'Member — tracks their own time',
}

/**
 * Whether one entry answers a search box.
 *
 * Takes resolved names rather than ids: a client is reached THROUGH the
 * project -- an entry stores a project and never a client -- so an entry with
 * no project cannot match a client name, and the caller is the one that knows
 * how to walk that.
 *
 * An empty query matches everything, so the box is a filter rather than a gate.
 */
export function entryMatches(
  query: string,
  fields: {
    desc?: string
    tags?: readonly string[]
    projectName?: string | null
    clientName?: string | null
  },
): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true

  const has = (v: string | null | undefined) => (v ?? '').toLowerCase().includes(q)

  return (
    has(fields.desc) ||
    has(fields.projectName) ||
    has(fields.clientName) ||
    (fields.tags ?? []).some((t) => t.toLowerCase().includes(q))
  )
}

/**
 * Why the workspace rules refuse to create time right now, or null if they do
 * not. The message is the one shown, so the reason and the wording cannot
 * drift apart.
 *
 * Takes the resolved project rather than an id, because a client is a property
 * of the project and never of the entry -- "require a client" can only mean
 * "the project chosen has to belong to one".
 *
 * Stopping a running timer is NOT checked against this. A rule switched on
 * while somebody is on the clock must not trap the time already recorded.
 */
export function blockedByRules(
  settings: Pick<Settings, 'requireProject' | 'requireClient'>,
  project: Pick<Project, 'name' | 'clientId'> | null | undefined,
): string | null {
  if (settings.requireProject && !project) {
    return 'Pick a project first — required by workspace rules.'
  }
  if (settings.requireClient) {
    if (!project) {
      return 'Pick a project with a client first — required by workspace rules.'
    }
    if (!project.clientId) {
      return `${project.name} has no client, and workspace rules require one. Give the project a client, or pick a different project.`
    }
  }
  return null
}

/** Owner and admin configure the workspace. */
export const canManage = (r: Role | undefined) => r === 'owner' || r === 'admin'
/** These roles also see, edit and export everyone's time. */
export const canSeeAll = (r: Role | undefined) => r === 'owner' || r === 'admin' || r === 'manager'

/**
 * May price people: set a charge-out rate, and a project's or task's price.
 *
 * This mirrors the DATABASE's is_admin(), which is true for owner, admin and
 * manager and whose own comment names rates and prices among the things it
 * governs. The RLS on user_hourly_rates has allowed a manager to write one
 * since it was written.
 *
 * It exists because canManage() is narrower -- owner and admin only -- and
 * the Team screen used that to decide who sees the rate control. The screen
 * was simply stricter than the rules it was enforcing, so a manager was shown
 * a rate they were allowed to change and no way to change it.
 */
export const canSetRates = (r: Role | undefined) =>
  r === 'owner' || r === 'admin' || r === 'manager'

export interface Project {
  id: ID
  name: string
  clientId: ID | null
  color: string
  billable: boolean
  rate: number
  estimate: number
  status: 'active' | 'archived'
  tasks: string[]
  /** Who made it, where that is known. See projects.created_by. */
  createdBy: ID | null
  /** ISO timestamp of creation. */
  createdAt: string | null
}

export interface Client {
  id: ID
  name: string
  contact: string
  status: 'active' | 'archived'
  /**
   * A would-be client, not a client.
   *
   * The two are never shown together: every list, picker, report grouping and
   * export is one or the other. Rather than leave each screen to remember
   * that, use `ofKind` below -- a surface that forgets to filter is the only
   * way the two can cross, and forgetting is easy.
   */
  isProspect: boolean
  /**
   * When this client was sent to the bin, or null while it is in use.
   *
   * A binned client is still here, and still resolves everywhere its name is
   * needed: the hours logged against its projects stay visible, stay in the
   * reports and stay in the exports, exactly as they were. What it loses is
   * its place in the lists and pickers that OFFER a client -- there is no
   * logging new time against something that has been deleted.
   *
   * That split is the whole point of the bin. Hiding the history as well
   * would make deleting a client silently delete the record of what was done
   * for them, which is the thing a bin exists to prevent.
   */
  deletedAt: string | null
  /** Who made it, where that is known. See clients.created_by. */
  createdBy: ID | null
  /** ISO timestamp of creation. */
  createdAt: string | null
}

/** Which of the two a surface is dealing with. There is never a third. */
export type ClientKind = 'client' | 'prospect'

/**
 * The clients of one kind, and only that kind -- binned ones included.
 *
 * Takes the kind rather than offering two predicates, and has no default, so
 * a caller is made to say which side it is on instead of getting one by
 * omission.
 *
 * This is the one to reach for when SHOWING what happened: a log, a report,
 * a total, an export. Those have to account for every hour that was worked,
 * and an hour does not stop having been worked because the client was later
 * deleted. Use `liveOfKind` where the question is which client to pick.
 */
export function ofKind(clients: readonly Client[], kind: ClientKind): Client[] {
  const wantProspect = kind === 'prospect'
  return clients.filter((c) => c.isProspect === wantProspect)
}

/**
 * The clients of one kind that are still in use.
 *
 * For anything that OFFERS a client: a picker, the Clients list, a filter
 * dropdown. Nothing new should be attached to a client that has been
 * deleted, and a binned one has no business in a list of choices.
 */
export function liveOfKind(clients: readonly Client[], kind: ClientKind): Client[] {
  return ofKind(clients, kind).filter((c) => c.deletedAt === null)
}

/** In the bin, either kind. The Clients screen is the only thing that asks. */
export function binned(clients: readonly Client[]): Client[] {
  return clients.filter((c) => c.deletedAt !== null)
}

/**
 * The ids of one kind, for filtering entries by the client behind a project.
 *
 * Binned included, deliberately: this decides which screen an entry belongs
 * to, and a binned client's time stays where it was rather than vanishing
 * from both.
 */
export function idsOfKind(clients: readonly Client[], kind: ClientKind): Set<ID> {
  return new Set(ofKind(clients, kind).map((c) => c.id))
}

/** A person in the workspace. Pending rows are invites that nobody has accepted yet. */
export interface Member {
  id: ID
  name: string
  email: string
  role: Role
  capacity: number
  /** False means this person charges nothing: it overrides the entry and project flags. */
  billable: boolean
  /** 'retired' means removed: kept so their past hours still name somebody. */
  status: 'active' | 'pending' | 'retired'
}

export interface Entry {
  id: ID
  userId: ID
  desc: string
  projectId: ID | null
  task: string
  tags: string[]
  billable: boolean
  day: DayKey
  /** Seconds since local midnight. */
  start: number
  /** Seconds. */
  dur: number
  /**
   * What the clock actually recorded, to the second. Written once when the
   * timer stops and immutable in the database thereafter, so an edited entry
   * can still show what really happened. Null for entries added by hand,
   * which were never timed.
   */
  trackedStart?: number | null
  trackedDur?: number | null
  /** Set when start or end was moved away from the tracked values. */
  editedAt?: number | null
  /**
   * Who last moved the reported times, when anybody did.
   *
   * Stamped by guard_tracked_times on every stop, including your own -- so
   * on its own it says nothing. Against userId it says everything: the two
   * differ only when somebody ELSE ended or amended this entry, which is the
   * case worth putting in front of the person whose time it is.
   */
  editedBy?: ID | null
  /**
   * An hourly rate for THIS entry, beating every rate below it.
   *
   * Set by a CSV import, or by an admin on the entry itself when somebody's
   * rate changed after the work was logged. Null means "use the usual rate".
   */
  rateOverride?: number | null
  /**
   * A flat fee for this entry. When set, the duration stops deciding the
   * money at all and every rate -- including rateOverride -- is ignored.
   */
  fixedAmount?: number | null
}

export interface RunningTimer {
  desc: string
  projectId: ID | null
  task: string
  billable: boolean
  /**
   * A flat fee already set on the running entry.
   *
   * It can be decided before the clock stops, because it does not depend on
   * how long the clock runs -- that is what makes it flat.
   */
  fixedAmount?: number | null
  /** Epoch ms, so the clock survives reloads and is shared across devices. */
  at: number
}

export interface Settings {
  weekStart: 'Monday' | 'Sunday'
  timeFormat: '12h' | '24h'
  durFormat: 'h:mm' | 'decimal'
  requireProject: boolean
  /**
   * The project chosen must belong to a client.
   *
   * Time attaches to a project and never to a client -- a client is reached
   * through the projects it owns -- so this is the only form "require a
   * client" can take. It implies requireProject: no project, no client.
   */
  requireClient: boolean
  autoStop: boolean
  /**
   * Round a stopped timer to the nearest minute.
   *
   * Replaces roundTo15, which was declared, defaulted, labelled "Applied when
   * a timer is stopped" -- and never implemented. Nothing rounded anything.
   */
  roundToMinute: boolean
  allowManual: boolean
}

/** Personal, per-member preference — not shared with the rest of the workspace. */
export interface Appearance {
  navLayout: 'sidebar' | 'topnav'
  timerStyle: 'bar' | 'card'
  reportsChart: 'bars' | 'line'
  accent: string
}

export interface WorkspaceRate {
  id: ID
  userId: ID
  hourlyRate: number
  currency: string
  effectiveFrom: DayKey
}

/** `by` is null once the approver's account has been deleted: the record of
 *  the submission outlives the person who made it. */
export type Submissions = Record<string, { at: number; by: ID | null }>

/** Everything the client needs to render one workspace. Returned by every mutation. */
export interface Workspace {
  id: ID
  name: string
  ownerId: ID
  /** Only sent to owner/admin — it grants access to anyone who has it. */
  joinCode: string | null
  /**
   * Whether people who sign themselves up land in this workspace's approval
   * queue. It defaults to false, and when it is off nothing ever reaches the
   * queue -- which is indistinguishable from nobody having applied unless the
   * screen is told which of the two it is looking at.
   */
  acceptsSignups: boolean
  settings: Settings
  appearance: Appearance
  myRole: Role
  myUserId: ID
  team: Member[]
  projects: Project[]
  clients: Client[]
  tags: string[]
  entries: Entry[]
  entriesTruncated: boolean
  /**
   * Charge-out rates, effective-dated. RLS decides the scope: admins get
   * everyone's, anyone else only their own, so reports simply show what the
   * viewer is allowed to price.
   */
  rates: WorkspaceRate[]
  running: Record<ID, RunningTimer | undefined>
  submissions: Submissions
}

export interface AuthUser {
  id: ID
  email: string
  name: string
  /** False for accounts created through Google that never set one. */
  hasPassword: boolean
  viaGoogle: boolean
}

/** What the server will accept, so the sign-in screen shows the right options. */
export interface AuthConfig {
  google: boolean
  googleDomain: string | null
  passwordLogin: boolean
}

export interface WorkspaceRef {
  id: ID
  name: string
  role: Role
}

export interface PendingInvite {
  id: ID
  role: Role
  workspaceId: ID
  workspaceName: string
}

export interface AuthPayload {
  user: AuthUser
  workspaces: WorkspaceRef[]
  invites: PendingInvite[]
}

export const SWATCHES = ['#DC5B34', '#2D5B8E', '#6B4E8F', '#2F6B4F', '#C08A2E', '#8E4B4B'] as const
export const DEFAULT_SETTINGS: Settings = {
  weekStart: 'Monday',
  timeFormat: '24h',
  durFormat: 'h:mm',
  requireProject: false,
  requireClient: false,
  autoStop: false,
  roundToMinute: false,
  allowManual: true,
}

export const DEFAULT_APPEARANCE: Appearance = {
  navLayout: 'sidebar',
  timerStyle: 'bar',
  reportsChart: 'bars',
  accent: '#C24A26',
}

export const ACCENTS = ['#C24A26', '#2D5B8E', '#2F6B4F', '#6B4E8F'] as const
